import { useState, useEffect } from 'react';
import { lexBotApi } from '../lib/lexBotApi';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Alert } from './ui/alert';
import { Badge } from './ui/badge';
import { Trash2, Plus, MessageSquare, ChevronDown, ChevronUp, Info } from 'lucide-react';

interface Intent {
  intentId: string;
  intentName: string;
  sampleUtterances: string[];
}

interface BotUtteranceEditorProps {
  tenantId: string;
  botId: string;
}

export function BotUtteranceEditor({ tenantId, botId }: BotUtteranceEditorProps) {
  const [intents, setIntents] = useState<Intent[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newUtterances, setNewUtterances] = useState<Record<string, string>>({});
  const [expandedIntents, setExpandedIntents] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadIntents();
  }, [tenantId, botId]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadIntents = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await lexBotApi.getIntents(tenantId);
      if (Array.isArray(data)) {
        setIntents(data);
        // Auto-expand first intent
        if (data.length > 0 && Object.keys(expandedIntents).length === 0) {
          setExpandedIntents({ [data[0].intentId]: true });
        }
      } else {
        setError('Invalid intents data format');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load intents');
    } finally {
      setLoading(false);
    }
  };

  const toggleIntent = (intentId: string) => {
    setExpandedIntents(prev => ({ ...prev, [intentId]: !prev[intentId] }));
  };

  const handleAddUtterance = async (intentId: string) => {
    const utterance = newUtterances[intentId]?.trim();
    if (!utterance) return;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await lexBotApi.addUtterance(tenantId, intentId, utterance);
      setNewUtterances({ ...newUtterances, [intentId]: '' });
      await loadIntents();
      setSuccess('✓ Utterance added');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add utterance');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveUtterance = async (intentId: string, utterance: string) => {
    if (!confirm(`Remove utterance: "${utterance}"?`)) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await lexBotApi.removeUtterance(tenantId, intentId, utterance);
      await loadIntents();
      setSuccess('✓ Utterance removed');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove utterance');
    } finally {
      setSaving(false);
    }
  };

  const formatIntentName = (name: string) => {
    return name.replace(/([A-Z])/g, ' $1').trim();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Loading intents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <div className="ml-2">
          <p className="font-semibold text-blue-900">Training Your AI Bot</p>
          <p className="text-sm text-blue-800 mt-1">
            Sample utterances teach your bot to recognize customer requests. Add 5-10 variations of how customers might phrase each question. 
            Changes take effect immediately in DRAFT mode.
          </p>
        </div>
      </Alert>

      {/* Status Messages */}
      {error && (
        <Alert variant="destructive">
          <p>{error}</p>
        </Alert>
      )}
      {success && (
        <Alert className="bg-green-50 border-green-200">
          <p className="text-green-800 font-medium">{success}</p>
        </Alert>
      )}

      {/* Summary Stats */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-gray-600" />
              <span className="font-medium">
                {intents.length} Intent{intents.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              {intents.reduce((sum, i) => sum + i.sampleUtterances.length, 0)} total utterances
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Intent Cards */}
      <div className="space-y-3">
        {intents.map((intent) => {
          const isExpanded = expandedIntents[intent.intentId];
          const utteranceCount = intent.sampleUtterances.length;
          
          return (
            <Card key={intent.intentId} className="overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleIntent(intent.intentId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                    <div>
                      <CardTitle className="text-base">
                        {formatIntentName(intent.intentName)}
                      </CardTitle>
                      <p className="text-xs text-gray-500 mt-1">
                        {utteranceCount} utterance{utteranceCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <Badge variant={utteranceCount >= 5 ? 'default' : 'secondary'}>
                    {utteranceCount >= 5 ? 'Well Trained' : 'Needs More'}
                  </Badge>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent className="pt-0 space-y-4">
                  {/* Existing Utterances */}
                  {intent.sampleUtterances.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Current Utterances
                      </p>
                      {intent.sampleUtterances.map((utterance, idx) => (
                        <div 
                          key={idx} 
                          className="group flex items-start gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <div className="flex-1 text-sm leading-relaxed pt-0.5">
                            "{utterance}"
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveUtterance(intent.intentId, utterance)}
                            disabled={saving}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <MessageSquare className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No utterances yet. Add your first one below.</p>
                    </div>
                  )}

                  {/* Add New Utterance */}
                  <div className="pt-2 border-t">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Add New Utterance
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder='e.g., "How many people are ahead of me?"'
                        value={newUtterances[intent.intentId] || ''}
                        onChange={(e) => setNewUtterances({ ...newUtterances, [intent.intentId]: e.target.value })}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddUtterance(intent.intentId)}
                        disabled={saving}
                        className="flex-1"
                      />
                      <Button
                        onClick={() => handleAddUtterance(intent.intentId)}
                        disabled={saving || !newUtterances[intent.intentId]?.trim()}
                        size="default"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Tip: Add variations like "What's my position?", "Where am I in line?", "How long until my turn?"
                    </p>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {intents.length === 0 && !loading && (
        <Card className="p-12">
          <div className="text-center">
            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Intents Found</h3>
            <p className="text-sm text-gray-600">
              Complete the bot setup wizard to copy intents from the shared bot.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
